/**
 * JulietteHistoryList — Liste des conversations (threads) reutilisable.
 *
 * Sert au tiroir mobile (JulietteHistoryDrawer) ET a la sidebar desktop.
 * Gere : nouvelle conversation, selection, renommer (inline) et supprimer
 * (confirmation inline) — facon ChatGPT.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, MessageSquare, Sunrise, Moon, Zap, Pencil, Trash2, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  listJulietteThreads,
  renameThread,
  deleteThread,
  sessionTypeLabel,
  type JulietteThreadSummary,
} from "@/lib/juliette-session";

interface JulietteHistoryListProps {
  teamId: string | null;
  enabled: boolean;
  currentSessionId: string | null;
  onSelect: (sessionId: string) => void;
  onNewChat: () => void;
  /** Notifie le parent quand une conversation est supprimee (pour gerer le cas "courante"). */
  onDeleted?: (sessionId: string) => void;
}

function iconForType(type: string) {
  switch (type) {
    case "briefing_matin":
      return Sunrise;
    case "debrief_soir":
      return Moon;
    case "reactivite":
      return Zap;
    default:
      return MessageSquare;
  }
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const min = Math.round(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.round(h / 24);
  if (d < 7) return `il y a ${d} j`;
  return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

function threadLabel(t: JulietteThreadSummary): string {
  if (t.title) return t.title;
  if (t.firstUserMessage) return t.firstUserMessage;
  if (t.summary) return t.summary.slice(0, 60);
  return sessionTypeLabel(t.sessionType);
}

export function JulietteHistoryList({
  teamId,
  enabled,
  currentSessionId,
  onSelect,
  onNewChat,
  onDeleted,
}: JulietteHistoryListProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: threads = [], isLoading } = useQuery({
    queryKey: ["juliette-threads", teamId],
    enabled: enabled && !!teamId,
    queryFn: () => listJulietteThreads(teamId as string),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["juliette-threads", teamId] });
  const onErr = (e: unknown) =>
    toast({ title: "Action impossible", description: e instanceof Error ? e.message : String(e), variant: "destructive" });

  const renameMutation = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => renameThread(id, title),
    onSuccess: refresh,
    onError: onErr,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteThread(id),
    onSuccess: (_data, id) => {
      refresh();
      onDeleted?.(id);
    },
    onError: onErr,
  });

  const busy = renameMutation.isPending || deleteMutation.isPending;

  return (
    <>
      <button
        type="button"
        onClick={onNewChat}
        className="mx-3 mb-2 flex items-center gap-2.5 rounded-2xl bg-gradient-to-r from-indigo-500/90 to-violet-600/90 px-4 py-3 text-sm font-medium text-white shadow-lg shadow-violet-950/30 hover:brightness-110 transition"
      >
        <Plus className="h-4 w-4" />
        Nouvelle conversation
      </button>

      <div className="flex-1 overflow-y-auto px-2 mt-1">
        {isLoading && <p className="px-3 py-4 text-sm text-white/40">Chargement…</p>}
        {!isLoading && threads.length === 0 && (
          <p className="px-3 py-4 text-sm text-white/40">Aucune conversation pour l'instant.</p>
        )}
        {threads.map((t) => (
          <ThreadRow
            key={t.id}
            thread={t}
            active={t.id === currentSessionId}
            busy={busy}
            onSelect={onSelect}
            onRename={(id, title) => renameMutation.mutate({ id, title })}
            onDelete={(id) => deleteMutation.mutate(id)}
          />
        ))}
      </div>
    </>
  );
}

function ThreadRow({
  thread,
  active,
  busy,
  onSelect,
  onRename,
  onDelete,
}: {
  thread: JulietteThreadSummary;
  active: boolean;
  busy: boolean;
  onSelect: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [confirming, setConfirming] = useState(false);
  const Icon = iconForType(thread.sessionType);
  const label = threadLabel(thread);

  const startEdit = () => {
    setValue(label);
    setEditing(true);
  };
  const save = () => {
    const v = value.trim();
    setEditing(false);
    if (v && v !== label) onRename(thread.id, v);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 rounded-xl px-3 py-2 bg-white/10">
        <Icon className="h-4 w-4 shrink-0 text-indigo-300/70" />
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              save();
            } else if (e.key === "Escape") {
              setEditing(false);
            }
          }}
          onBlur={save}
          className="flex-1 min-w-0 bg-transparent text-sm text-white focus:outline-none border-b border-white/30"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "group flex items-center rounded-xl pr-1 transition-colors",
        active ? "bg-white/10" : "hover:bg-white/[0.06]",
      )}
    >
      <button
        type="button"
        onClick={() => onSelect(thread.id)}
        className="flex-1 min-w-0 flex items-start gap-3 px-3 py-2.5 text-left"
      >
        <Icon className="h-4 w-4 mt-0.5 shrink-0 text-indigo-300/70" />
        <span className="flex-1 min-w-0">
          <span className="block truncate text-sm text-white/85">{label}</span>
          <span className="block text-[11px] text-white/35 mt-0.5">
            {sessionTypeLabel(thread.sessionType)} · {formatRelative(thread.startedAt)}
          </span>
        </span>
      </button>

      {confirming ? (
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            disabled={busy}
            onClick={() => onDelete(thread.id)}
            aria-label="Confirmer la suppression"
            title="Supprimer définitivement"
            className="h-7 w-7 rounded-md flex items-center justify-center text-rose-300 hover:bg-rose-500/15 disabled:opacity-40"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            aria-label="Annuler"
            className="h-7 w-7 rounded-md flex items-center justify-center text-white/50 hover:bg-white/10"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className="flex shrink-0 items-center gap-0.5 opacity-50 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={startEdit}
            aria-label="Renommer"
            title="Renommer"
            className="h-7 w-7 rounded-md flex items-center justify-center text-white/40 hover:bg-white/10 hover:text-white/80"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            aria-label="Supprimer"
            title="Supprimer"
            className="h-7 w-7 rounded-md flex items-center justify-center text-white/40 hover:bg-white/10 hover:text-rose-300"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
